import { useMemo, useRef, useCallback } from 'react'
import { css } from '@emotion/react'
import {
  oneOfType,
  node,
  object,
  bool,
  func,
  string,
  shape,
  array,
} from 'prop-types'
import { useInView } from 'react-intersection-observer'

import mediaQueries from '@/layout/media-queries'
import { DEVELOPER_PAGE_TABS } from '@/constants'
import Sidebar from '@/components/Sidebar'
import Header from './Header'
import Products from './Products'
import Contact from './Contact'

const wrapperCss = css`
  width: 100%;

  ${mediaQueries.smallScreen()} {
    padding-right: 32px;
  }

  ${mediaQueries.mediumScreen()} {
    padding-right: 36px;
  }
  ${mediaQueries.xlargeScreen()} {
    padding-right: 60px;
  }
`

const container = css`
  display: flex;
  width: 100%;
  height: 100%;
  margin-top: 30px;

  ${mediaQueries.smallScreen()} {
    margin-right: 32px;
    margin-left: 32px;
  }

  ${mediaQueries.mediumScreen()} {
    margin-left: 0;
  }

  ${mediaQueries.xlargeScreen()} {
    margin-right: 60px;
  }
`

const Developer = ({
  developer,
  hideSidebar,
  sidebarComponent: SidebarComponent = Sidebar,
  className,
}) => {
  const { company, apps, github, contactEmail, twitter, bio, website } =
    developer

  const headerRef = useRef(null)
  let topMargin = 0

  if (headerRef.current) {
    const { height } = headerRef.current.getBoundingClientRect()
    topMargin = 90 + height
  }

  const [appsRef, inAppsView] = useInView({
    rootMargin: `-${topMargin}px 0px 0px 0px`,
  })

  const [contactRef, inContactView] = useInView({
    rootMargin: `-${topMargin}px 0px 0px 0px`,
  })

  const updateUrl = useCallback((url) => {
    if (window.location.hash !== url) {
      // Safari has a limitation (100 times per 30 secs)
      // This is very unlikely to happen but possible
      try {
        window.history.replaceState(null, null, url)
      } catch {}
    }
  }, [])

  const activeTab = useMemo(() => {
    if (inAppsView) {
      updateUrl(`#${DEVELOPER_PAGE_TABS.APPS}`)
      return DEVELOPER_PAGE_TABS.APPS
    } else if (inContactView) {
      updateUrl(`#${DEVELOPER_PAGE_TABS.CONTACT}`)
      return DEVELOPER_PAGE_TABS.CONTACT
    } else {
      return null
    }
  }, [updateUrl, inAppsView, inContactView])

  const tabs = useMemo(() => {
    const hasApps = apps.length > 0
    const hasContact = contactEmail || twitter || github

    return [
      { id: DEVELOPER_PAGE_TABS.APPS, title: 'Apps' },
      { id: DEVELOPER_PAGE_TABS.CONTACT, title: 'Contact' },
    ].filter((tab) => {
      if (tab.id === DEVELOPER_PAGE_TABS.APPS && !hasApps) {
        return false
      } else if (tab.id === DEVELOPER_PAGE_TABS.CONTACT && !hasContact) {
        return false
      }
      return true
    })
  }, [apps, contactEmail, twitter, github])
  return (
    <div css={container} className="developer-wrapper">
      {!hideSidebar && <SidebarComponent className="sidebar" />}
      <div css={wrapperCss} className={`body ${className || ''}`}>
        <Header
          tabs={tabs}
          activeTab={activeTab}
          name={company}
          description={bio}
          ref={headerRef}
        />
        {apps.length > 0 && (
          <Products
            products={apps}
            innerRef={appsRef}
            type="Apps"
            id={DEVELOPER_PAGE_TABS.APPS}
          />
        )}
        {(contactEmail || twitter || github || website) && (
          <Contact
            github={github}
            twitter={twitter}
            website={website}
            company={company}
            contactEmail={contactEmail}
            innerRef={contactRef}
            id={DEVELOPER_PAGE_TABS.CONTACT}
          />
        )}
      </div>
    </div>
  )
}

Developer.propTypes = {
  developer: shape({
    bio: string,
    contactEmail: string,
    twitter: string,
    website: string,
    apps: array,
    services: array,
    company: string,
  }),
  hideSidebar: bool,
  sidebarComponent: oneOfType([node, func, object]),
  className: string,
}
export default Developer
